import { useCallback, useEffect, useRef, useState } from 'react';
import { findConversation } from '@/lib/conversation-api';
import { getOrCreateVisitorId, loadConversationRef, saveConversationRef, } from '@/lib/conversation-storage';
export default function useConversationPersistence(appConfig, hasActiveSession) {
    const widgetId = appConfig.webWidgetId || 'default';
    const visitorId = useRef(getOrCreateVisitorId()).current;
    const [conversationId, setConversationId] = useState(null);
    const [dbMessages, setDbMessages] = useState([]);
    const [isFollowup, setIsFollowup] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [visitorEmail, setVisitorEmail] = useState(null);
    const [visitorPhone, setVisitorPhone] = useState(null);
    const [visitorName, setVisitorName] = useState(null);
    // Load on mount — read-only check for resumable conversation
    useEffect(() => {
        let ref = loadConversationRef(widgetId);
        if (ref) {
            setVisitorEmail(ref.visitorEmail || null);
            setVisitorPhone(ref.visitorPhone || null);
            setVisitorName(ref.visitorName || null);
        }
        if (hasActiveSession) {
            // Page-nav session is active — just load conversationId for metadata
            if (ref) {
                setConversationId(ref.conversationId);
            }
            return;
        }
        // Ensure a conversation ref exists so future visits can look up conversations.
        // Without this, follow-up never works when email collection is disabled.
        if (!ref) {
            ref = { conversationId: '', visitorId, lastActivity: Date.now() };
            saveConversationRef(ref, widgetId);
        }
        // Check for resumable conversation from DB
        setIsLoading(true);
        findConversation(widgetId, ref.visitorId, ref.visitorEmail)
            .then((response) => {
            if (response.isResumed && response.conversationId) {
                setConversationId(response.conversationId);
                setIsFollowup(true);
                if (response.messages.length > 0) {
                    const restored = response.messages.map((m, i) => ({
                        id: m.id || `db_${response.conversationId}_${i}`,
                        timestamp: typeof m.timestamp === 'number' ? m.timestamp : new Date(m.timestamp).getTime(),
                        message: m.content,
                        isLocal: m.senderType === 'visitor' || m.role === 'user',
                    }));
                    setDbMessages(restored);
                }
                // Update the ref with the found conversation
                saveConversationRef(Object.assign(Object.assign({}, ref), { conversationId: response.conversationId, lastActivity: Date.now() }), widgetId);
            }
            else {
                // No resumable conversation (completed or expired) — clear old conversationId
                // but keep the ref with visitorId so future lookups still work.
                saveConversationRef(Object.assign(Object.assign({}, ref), { conversationId: '', lastActivity: Date.now() }), widgetId);
            }
        })
            .catch((err) => {
            console.warn('[conversation-persistence] Failed to check for resumable conversation:', err);
        })
            .finally(() => setIsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Re-check for incomplete conversations on demand (e.g. when popup reopens without page refresh)
    const recheckForIncomplete = useCallback(async () => {
        const ref = loadConversationRef(widgetId);
        if (!ref)
            return false;
        try {
            const response = await findConversation(widgetId, ref.visitorId, ref.visitorEmail);
            if (response.isResumed && response.conversationId) {
                setConversationId(response.conversationId);
                setIsFollowup(true);
                if (response.messages.length > 0) {
                    const restored = response.messages.map((m, i) => ({
                        id: m.id || `db_${response.conversationId}_${i}`,
                        timestamp: typeof m.timestamp === 'number' ? m.timestamp : new Date(m.timestamp).getTime(),
                        message: m.content,
                        isLocal: m.senderType === 'visitor' || m.role === 'user',
                    }));
                    setDbMessages(restored);
                }
                saveConversationRef(Object.assign(Object.assign({}, ref), { conversationId: response.conversationId, lastActivity: Date.now() }), widgetId);
                return true;
            }
            return false;
        }
        catch (_a) {
            return false;
        }
    }, [widgetId]);
    // Clear followup state when user chooses "Start fresh"
    const clearFollowup = useCallback(() => {
        setConversationId(null);
        setDbMessages([]);
        setIsFollowup(false);
    }, []);
    const setVisitorInfo = useCallback((email, name) => {
        setVisitorEmail(email);
        if (name)
            setVisitorName(name);
        // Save to localStorage so connection-details can read it
        const existing = loadConversationRef(widgetId);
        saveConversationRef({
            conversationId: (existing === null || existing === void 0 ? void 0 : existing.conversationId) || conversationId || '',
            visitorId,
            visitorEmail: email,
            visitorPhone: existing === null || existing === void 0 ? void 0 : existing.visitorPhone,
            visitorName: name !== null && name !== void 0 ? name : existing === null || existing === void 0 ? void 0 : existing.visitorName,
            lastActivity: Date.now(),
        }, widgetId);
    }, [widgetId, visitorId, conversationId]);
    const setVisitorContact = useCallback((contact) => {
        var _a, _b, _c;
        if (contact.email !== undefined)
            setVisitorEmail(contact.email || null);
        if (contact.phone !== undefined)
            setVisitorPhone(contact.phone || null);
        if (contact.name !== undefined)
            setVisitorName(contact.name || null);
        const existing = loadConversationRef(widgetId);
        saveConversationRef({
            conversationId: (existing === null || existing === void 0 ? void 0 : existing.conversationId) || conversationId || '',
            visitorId,
            visitorEmail: (_a = contact.email) !== null && _a !== void 0 ? _a : existing === null || existing === void 0 ? void 0 : existing.visitorEmail,
            visitorPhone: (_b = contact.phone) !== null && _b !== void 0 ? _b : existing === null || existing === void 0 ? void 0 : existing.visitorPhone,
            visitorName: (_c = contact.name) !== null && _c !== void 0 ? _c : existing === null || existing === void 0 ? void 0 : existing.visitorName,
            lastActivity: Date.now(),
        }, widgetId);
    }, [widgetId, visitorId, conversationId]);
    return {
        conversationId,
        visitorId,
        dbMessages,
        isFollowup,
        isLoading,
        visitorEmail,
        visitorPhone,
        visitorName,
        setVisitorInfo,
        setVisitorContact,
        recheckForIncomplete,
        clearFollowup,
    };
}
